import React, { useState, useMemo, useEffect } from "react";
import { Modal, Button, Spinner, Nav } from "react-bootstrap";
import {
  PDFViewer,
  pdf,
  Document,
  Page,
  View,
  Image,
  StyleSheet,
  Text,
} from "@react-pdf/renderer";
import { getAuditLevelLabel, getTodayISODate } from "../../utils";
import A360Logo from "../../icons/A360Logo.png";
import { getAuditReivewDetails, getReportReviewData } from "../../api/common";

// Props
interface Props {
  show: boolean;
  id: any;
  status: string | null;
  onClose: () => void;
}

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 9,
    lineHeight: 1.3,
    paddingTop: 70,
    paddingBottom: 80,
    paddingHorizontal: 20,
    backgroundColor: "#f8f9fa",
    color: "#1a1a1a",
  },
  header: {
    position: "absolute",
    top: 15,
    left: 20,
    right: 20,
    flexDirection: "row",
    alignItems: "center",
    borderBottom: "3px solid #283b69",
    paddingBottom: 10,
  },
  logo: { width: "auto", height: 36, marginRight: 200 },
  reportTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#283b69",
    marginTop: 10,
    marginBottom: 20,
  },
  reportTitle2: { fontSize: 16, fontWeight: "bold", color: "#283b69" },
  date: { fontSize: 7.5, color: "#666", textAlign: "right" },
  summaryTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#283b69",
    marginBottom: 12,
    marginTop: 15,
  },
  summaryTable: {
    backgroundColor: "#fff",
    border: "1px solid #dee2e6",
    borderRadius: 8,
    overflow: "hidden",
    marginBottom: 10,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#283b69",
    color: "#fff",
    borderBottom: "2px solid #283b69",
  },
  headerCellCenter: {
    flex: 1,
    fontSize: 7.5,
    fontWeight: "bold",
    textAlign: "center",
    padding: 8,
    borderRight: "1px solid #283b69",
    justifyContent: "center",
  },
  headerCellLastCenter: {
    flex: 1,
    fontSize: 7.5,
    fontWeight: "bold",
    textAlign: "center",
    padding: 8,
    borderRight: "none",
    justifyContent: "center",
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1px solid #dee2e6",
    minHeight: 28,
  },
  cell: {
    flex: 1,
    fontSize: 7.5,
    padding: 6,
    borderRight: "1px solid #dee2e6",
  },
  cellCenter: {
    flex: 1,
    fontSize: 7.5,
    padding: 6,
    textAlign: "center",
    borderRight: "1px solid #dee2e6",
  },
  cellLastCenter: { flex: 1, fontSize: 7.5, padding: 6, textAlign: "center" },
  cellLast: { flex: 1, fontSize: 7.5, padding: 6, textAlign: "left" },
  multiLineText: { fontSize: 7.5, lineHeight: 1.2 },
  multiLineTextWrap: { fontSize: 7.5, lineHeight: 1.2, whiteSpace: "pre-wrap" },
  detailsSection: {
    backgroundColor: "#fff",
    border: "1px solid #dee2e6",
    borderRadius: 8,
    padding: 12,
    marginBottom: 20,
  },
  detailRow: { flexDirection: "row", marginBottom: 6 },
  detailLabel: {
    width: 140,
    fontWeight: "bold",
    color: "#283b69",
    fontSize: 9,
  },
  detailValue: { flex: 1, fontSize: 9 },
  footer: {
    position: "absolute",
    bottom: 15,
    left: 20,
    right: 20,
    textAlign: "right",
    fontSize: 7,
    color: "#6c757d",
    borderTop: "1px solid #dee2e6",
    paddingTop: 6,
  },

  // Question table layout
  questionTableHeader: {
    flexDirection: "row",
    backgroundColor: "#283b69",
    color: "#fff",
  },
  questionCellCenter: {
    flex: 1.3,
    fontSize: 7.5,
    padding: 6,
    textAlign: "center",
    flexShrink: 1,
    lineHeight: 1.2,
    borderRight: "1px solid #dee2e6",
    wordBreak: "keep-all",
    whiteSpace: "nowrap",
  },
  questionCell: {
    flex: 2.5,
    fontSize: 7.5,
    padding: 6,
    textAlign: "center",
    flexWrap: "wrap",
    flexShrink: 1,
    lineHeight: 1.2,
    borderRight: "1px solid #dee2e6",
  },
  questionCellLastCenter: {
    flex: 1.2,
    fontSize: 7.5,
    padding: 6,
    textAlign: "center",
    flexShrink: 1,
    lineHeight: 1.2,
    wordBreak: "keep-all",
    whiteSpace: "nowrap",
  },
});

// 🧾 PDF document builder
const getPDFDocument = (
  orgs: any[],
  logoUrl: any,
  questionRowData: any,
  summaryOnly = false,
  auditPref: any,
  auditors: any[],
  auditCode: string,
  auditdetails: any,
  questiondetails: any
) => (
  <Document>
    {/* Page 1 */}
    <Page size="A4" style={styles.page}>
      <View style={styles.header} fixed wrap={false}>
        <Image source={logoUrl} style={styles.logo} />
      </View>

      <Text style={styles.summaryTitle}>
        Audit Summary Report :: {auditCode}
      </Text>

      {/* Auditee Table */}
      <Text style={styles.summaryTitle}>Auditee Info</Text>
      <View style={styles.summaryTable}>
        <View style={styles.tableHeader}>
          <Text style={styles.headerCellCenter}>Industry/ Sector</Text>
          <Text style={styles.headerCellCenter}>Organization</Text>
          <Text style={styles.headerCellCenter}>Organization Email</Text>
          <Text style={styles.headerCellCenter}>Auditee Team Lead</Text>
          <Text style={styles.headerCellLastCenter}>Auditee Members</Text>
        </View>
        {orgs.map((org, i) => (
          <View key={`auditee-${i}`} style={styles.tableRow}>
            <View style={styles.cell}>
              <Text style={styles.multiLineText}>{org.industry || "-"}</Text>
            </View>
            <View style={styles.cell}>
              <Text style={styles.multiLineText}>
                {org.organizationName || "-"}
              </Text>
            </View>
            <View style={styles.cell}>
              <Text style={styles.multiLineText}>
                {org.organizationEmail || "-"}
              </Text>
            </View>
            <View style={styles.cell}>
              <Text style={styles.multiLineText}>
                {(org.auditeeTeamLead || []).join(", ") || "-"}
              </Text>
            </View>
            <View style={styles.cellLastCenter}>
              <Text style={styles.multiLineText}>
                {(org.auditeeMembers || []).join(", ") || "-"}
              </Text>
            </View>
          </View>
        ))}
      </View>

      {/* Audit Preference Table */}
      <Text style={styles.summaryTitle}>Audit Preference</Text>
      <View style={styles.summaryTable}>
        <View style={styles.tableHeader}>
          <Text style={styles.headerCellCenter}>Audit Mode</Text>
          <Text style={styles.headerCellCenter}>Audit Level</Text>
          <Text style={styles.headerCellCenter}>Turnaround Day(s)</Text>
          <Text style={styles.headerCellCenter}>Start Date</Text>
          <Text style={styles.headerCellCenter}>End Date</Text>
          <Text style={styles.headerCellCenter}>Kickoff Date</Text>
          <Text style={styles.headerCellCenter}>Audit Themes</Text>
        </View>
        <View style={styles.tableRow}>
          <View style={styles.cell}>
            <Text style={styles.multiLineText}>{auditPref.mode}</Text>
          </View>
          <View style={styles.cell}>
            <Text style={styles.multiLineText}>{auditPref.level}</Text>
          </View>
          <View style={styles.cellCenter}>
            <Text style={styles.multiLineText}>{auditPref.turnaround}</Text>
          </View>
          <View style={styles.cellCenter}>
            <Text style={styles.multiLineText}>{auditPref.startDate}</Text>
          </View>
          <View style={styles.cellCenter}>
            <Text style={styles.multiLineText}>{auditPref.endDate}</Text>
          </View>
          <View style={styles.cellCenter}>
            <Text style={styles.multiLineText}>{auditPref.kickOffDate}</Text>
          </View>
          <View style={styles.cell}>
            <Text style={styles.multiLineText}>{auditPref.theme}</Text>
          </View>
        </View>
      </View>

      {/* Auditor Table */}
      <Text style={styles.summaryTitle}>Auditor Info</Text>
      <View style={styles.summaryTable}>
        <View style={styles.tableHeader}>
          <Text style={styles.headerCellCenter}>Auditor Name</Text>
          <Text style={styles.headerCellLastCenter}>Auditor Team Members</Text>
          <Text style={styles.headerCellLastCenter}>Response Time</Text>
        </View>
        <View style={styles.tableRow}>
          <View style={styles.cell}>
            <Text style={styles.multiLineText}>
              {auditdetails.auditorName || "-"}
            </Text>
          </View>
          <View style={styles.cell}>
            <Text style={styles.multiLineTextWrap}>
              {(auditdetails.auditorMembers || [])
                .map((member: any) => `${member.name}`)
                .join("\n") || "-"}
            </Text>
          </View>
          <View style={styles.cellLastCenter}>
            <Text style={styles.multiLineText}>
              {auditdetails.turnaroundTime
                ? `${auditdetails.turnaroundTime} Days`
                : "-"}
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.footer} fixed>
        <Text>{`Confidential Audit Report | © Source A360 | ${getTodayISODate()}`}</Text>
      </View>
    </Page>

    {/* Page 2 — Questionnaire */}
    {!summaryOnly && (
      <Page size="A4" style={styles.page} wrap={false}>
        <View style={styles.header}>
          <Text style={styles.reportTitle2}>Questionnaire Responses</Text>
        </View>
        {questiondetails.organizations?.map((org: any, orgIndex: number) => {
          const orgResponses = org.questionsAndResponses || {};
          const questions = orgResponses.questions || [];
          const responses = orgResponses.questionResponses || {};

          return (
            <View key={`org-${orgIndex}`} style={{ marginBottom: 25 }}>
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: "bold",
                  color: "#0D1A5B",
                  marginBottom: 8,
                  textDecoration: "underline",
                }}
              >
                {org.organizationName}
              </Text>

              {/* Loop through each question for this organization */}
              {questions.map((q: any, qi: number) => {
                const grouped = responses[q.id] || {};
                const responders = Object.keys(grouped);

                return (
                  <View key={q.id} style={{ marginBottom: 20 }}>
                    {/* Question */}
                    <Text
                      style={{
                        fontSize: 10,
                        fontWeight: "bold",
                        color: "#283b69",
                        marginBottom: 6,
                      }}
                    >
                      {qi + 1}. {q.question}
                    </Text>

                    {/* Responder groups */}
                    {responders.map((responder, ri) => {
                      const respList = grouped[responder];
                      let serialNumber = 1;

                      return (
                        <View
                          key={`${q.id}-${responder}`}
                          style={{ marginBottom: 10 }}
                          wrap={false}
                        >
                          {/* Responder Name */}
                          <Text
                            style={{
                              fontSize: 9,
                              fontWeight: "bold",
                              marginBottom: 3,
                              color: "#333",
                            }}
                          >
                            {responder}
                          </Text>

                          {/* Existing Table Format */}
                          <View style={styles.summaryTable}>
                            <View style={styles.questionTableHeader}>
                              <Text
                                style={[
                                  styles.questionCellCenter,
                                  { flex: 0.6 },
                                ]}
                              >
                                S/No
                              </Text>
                              <Text style={styles.questionCell}>Question</Text>
                              <Text style={styles.questionCellCenter}>
                                Auditee Response
                              </Text>
                              <Text style={styles.questionCellCenter}>
                                Compliance
                              </Text>
                              <Text style={styles.questionCellCenter}>
                                Reason
                              </Text>
                              <Text style={styles.questionCellCenter}>
                                Documents
                              </Text>
                              <Text style={styles.questionCellLastCenter}>
                                Responded On
                              </Text>
                            </View>

                            {respList.map((resp: any, idx: number) => {
                              const isFollowUp =
                                idx > 0 ? `Follow Up (${idx})` : "";
                              const displayQuestion =
                                resp.question || q.question;

                              return (
                                <View key={idx} style={styles.tableRow}>
                                  <View
                                    style={[
                                      styles.questionCellCenter,
                                      { flex: 0.6 },
                                    ]}
                                  >
                                    <Text>
                                      {isFollowUp ? isFollowUp : serialNumber++}
                                    </Text>
                                  </View>
                                  <View style={styles.questionCell}>
                                    <Text>{displayQuestion}</Text>
                                  </View>
                                  <View style={styles.questionCellCenter}>
                                    <Text>
                                      {resp.response || "Awaiting Response"}
                                    </Text>
                                  </View>
                                  <View style={styles.questionCellCenter}>
                                    {resp.response ? (
                                      <Text
                                        style={{
                                          color: resp.complianceStatus
                                            ? "#198754"
                                            : "#d93025",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        {resp.complianceStatus
                                          ? "Satisfied"
                                          : "Not Satisfied"}
                                      </Text>
                                    ) : (
                                      <Text>-</Text>
                                    )}
                                  </View>
                                  <View style={styles.questionCellCenter}>
                                    <Text>{resp.reason || "-"}</Text>
                                  </View>
                                  <View style={styles.questionCellCenter}>
                                    <Text>
                                      {Array.isArray(resp.documents) &&
                                        resp.documents.length > 0
                                        ? resp.documents.join(", ")
                                        : "-"}
                                    </Text>
                                  </View>
                                  <View style={styles.questionCellLastCenter}>
                                    <Text>
                                      {resp.respondedOn
                                        ? new Date(
                                          resp.respondedOn
                                        ).toLocaleString()
                                        : "-"}
                                    </Text>
                                  </View>
                                </View>
                              );
                            })}
                          </View>
                        </View>
                      );
                    })}
                  </View>
                );
              })}
            </View>
          );
        })}
        <View style={styles.footer} fixed>
          <Text>{`Confidential Audit Report | © Source A360 | ${getTodayISODate()}`}</Text>
        </View>
      </Page>
    )}
  </Document>
);

const AuditPDFPreviewModal: React.FC<Props> = ({ show, onClose, id, status }) => {
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState<"summary" | "detailed">("summary");
  const [auditdetails, setAuditDetails] = useState<any>(null);
  const [questiondetails, setQuestionDetails] = useState<any>(null);
  useEffect(() => {
    if (show) {
      getAuditReivewDetails(parseInt(id!)).then((response: any) => {
        setAuditDetails(response);
      });
      getReportReviewData(id).then((response: any) => {
        setQuestionDetails(response);
      });
    }
  }, [show]);

  const pdfDocument = useMemo(() => {
    if (!auditdetails) return null;

    const mappedOrgs = (auditdetails.organizations || []).map((org: any) => ({
      industry: org.industryOrSector || "-",
      organizationName: org.organizationName || "-",
      organizationEmail: org.organizationEmail || "-",
      auditeeTeamLead: (org.auditeeTeamLead || []).map((tl: any) => tl.name),
      auditeeMembers: (org.auditeeMembers || []).map((m: any) => m.name),
    }));

    const auditPref = {
      mode: auditdetails.auditMode === 1 ? "Auto Pilot" : "Manual",
      level: getAuditLevelLabel(auditdetails.auditLevel),
      turnaround: auditdetails.turnaroundTime,
      startDate: auditdetails.startDate || "-",
      endDate: auditdetails.endDate || "-",
      kickOffDate: auditdetails.kickOffDate || "-",
      theme: (auditdetails.auditTheme || []).map((t: any) => `${t.themeName} (${t.themeCode})`).join(", "),
    };

    const auditors = auditdetails.auditorMembers || [];

    return getPDFDocument(
      mappedOrgs,
      A360Logo,
      { questions: [], questionResponses: {} },
      activeTab === "summary",
      auditPref,
      auditors,
      auditdetails.auditCode,
      auditdetails,
      questiondetails
    );
  }, [auditdetails, activeTab]);

  const handleDownload = async () => {
    if (!pdfDocument) return;
    setDownloading(true);
    try {
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Audit Summary Report-${new Date().toISOString().split("T")[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Audit Report</Modal.Title>
      </Modal.Header>

      <style>{`
        .custom-tabs .nav-link {
          color: black;
          border: 1px solid #003366;
          margin-right: 2px;
          border-radius: 4px 4px 0 0;
          background-color: white;
        }
        .custom-tabs .nav-link.active {
          background-color: #003366;
          color: white !important;
          border-color: #003366;
        }
      `}</style>

      {status !== "Planning" && <Nav
        className="my-2 mx-2 custom-tabs"
        variant="tabs"
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k as any)}
      >
        <Nav.Item>
          <Nav.Link className="mx-2" eventKey="summary">
            Summary
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="detailed">Detailed Report</Nav.Link>
        </Nav.Item>
      </Nav>}

      <Modal.Body style={{ height: "70vh", padding: 10 }}>
        {pdfDocument && (
          <PDFViewer
            width="100%"
            height="100%"
            style={{ borderRadius: "8px" }}
            showToolbar={false}
          >
            {pdfDocument}
          </PDFViewer>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="success"
          onClick={handleDownload}
          disabled={downloading || !pdfDocument}
          style={{ backgroundColor: "#003366", borderColor: "#003366" }}
        >
          {downloading ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              Generating...
            </>
          ) : (
            "Download PDF"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AuditPDFPreviewModal;
